import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { FinancialDashboard } from '../../components/ui/financial-dashboard';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Ship, Plus, X, Loader2, Trash2, Download, Send, Check, Eye,
  FileSignature, Calendar, CreditCard, ChevronDown, ChevronUp,
  Banknote, Copy, User, Mail, Phone, MapPin, Shield, Hash, Anchor, Pencil, CopyPlus, TrendingUp, RotateCcw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  draft: { style: 'bg-slate-100 text-slate-700 border border-slate-300', label: 'Brouillon' },
  sent: { style: 'bg-blue-100 text-blue-800 border border-blue-300', label: 'Envoyé' },
  signed_owner: { style: 'bg-amber-100 text-amber-800 border border-amber-300', label: 'Signé propriétaire' },
  signed_exploitant: { style: 'bg-indigo-100 text-indigo-800 border border-indigo-300', label: 'Signé exploitant' },
  signed: { style: 'bg-emerald-100 text-emerald-800 border border-emerald-300', label: 'Signé (complet)' },
};

function formatDateTimeFr(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatMoney(n) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);
}

const MOIS_FR = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
function formatDateFr(dateStr) {
  if (!dateStr) return '';
  try {
    let y, m, d;
    if (dateStr.includes('-') && dateStr.length === 10 && dateStr[4] === '-') {
      [y, m, d] = dateStr.split('-').map(Number);
    } else if (dateStr.includes('/')) {
      const parts = dateStr.split('/').map(Number);
      if (parts[0] > 31) { [y, m, d] = parts; } else { [d, m, y] = parts; }
    } else { return dateStr; }
    const dayStr = d === 1 ? '1er' : String(d);
    return `${dayStr} ${MOIS_FR[m - 1]} ${y}`;
  } catch { return dateStr; }
}

// Convert DD/MM/YYYY to YYYY-MM-DD for input[type=date]
function toInputDate(dateStr) {
  if (!dateStr) return '';
  if (dateStr.includes('-') && dateStr.length === 10 && dateStr[4] === '-') return dateStr;
  if (dateStr.includes('/')) {
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      if (parts[0].length === 4) return `${parts[0]}-${parts[1].padStart(2,'0')}-${parts[2].padStart(2,'0')}`;
      return `${parts[2]}-${parts[1].padStart(2,'0')}-${parts[0].padStart(2,'0')}`;
    }
  }
  return dateStr;
}

// ─── CREATE/EDIT FORM ───
function ContractForm({ onDone, onCancel, initialData }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [saving, setSaving] = useState(false);
  const isEdit = !!initialData;

  const [form, setForm] = useState({
    season: initialData?.season || '2026',
    start_date: toInputDate(initialData?.start_date) || '',
    end_date: toInputDate(initialData?.end_date) || '',
    owner_name: initialData?.owner?.name || '',
    owner_address: initialData?.owner?.address || '',
    owner_email: initialData?.owner?.email || '',
    owner_phone: initialData?.owner?.phone || '',
    owner_insurance_company: initialData?.owner?.insurance_company || '',
    owner_insurance_number: initialData?.owner?.insurance_number || '',
    vessel_name: initialData?.vessel?.name || '',
    vessel_model: initialData?.vessel?.model || '',
    vessel_year: initialData?.vessel?.year || '',
    vessel_registration: initialData?.vessel?.registration || '',
    vessel_win_number: initialData?.vessel?.win_number || '',
    vessel_home_port: initialData?.vessel?.home_port || '',
    total_amount: initialData?.total_amount || '',
    total_amount_words: initialData?.total_amount_words || '',
    navigation_zone: initialData?.navigation_zone || "Côte d'Azur, Corse et Sardaigne",
    jurisdiction: initialData?.jurisdiction || 'Ajaccio',
    location: initialData?.location || 'Ajaccio',
    // Exploitant defaults
    exploitant_name: initialData?.exploitant?.name || 'Nicolas LLORENS',
    exploitant_company: initialData?.exploitant?.company || 'SOGNUDIMARE',
    exploitant_address: initialData?.exploitant?.address || '160 rue des Espadons, 20000 Ajaccio',
    exploitant_siret: initialData?.exploitant?.siret || '451 387 369 00031',
    exploitant_ape: initialData?.exploitant?.ape || '52.22Z',
    exploitant_insurance_company: initialData?.exploitant?.insurance_company || 'PANTAENIUS',
    exploitant_insurance_number: initialData?.exploitant?.insurance_number || '121801192-11',
    exploitant_sailor_number: initialData?.exploitant?.sailor_number || '20175205R',
    exploitant_patent: initialData?.exploitant?.patent || '2380-00-01567800',
  });

  const [payments, setPayments] = useState(
    initialData?.payment_schedule?.length
      ? initialData.payment_schedule.map(p => ({ amount: String(p.amount || ''), label: p.label || '', due_date: p.due_date || '' }))
      : [{ amount: '', label: '', due_date: '' }, { amount: '', label: '', due_date: '' }]
  );

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setPayment = (i, k, v) => {
    const next = [...payments];
    next[i] = { ...next[i], [k]: v };
    setPayments(next);
  };

  const addPayment = () => setPayments(prev => [...prev, { amount: '', label: '', due_date: '' }]);
  const removePayment = (i) => setPayments(prev => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.owner_name || !form.vessel_name || !form.total_amount) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        total_amount: parseFloat(form.total_amount) || 0,
        payment_schedule: payments.filter(p => p.amount).map(p => ({
          amount: parseFloat(p.amount) || 0,
          label: p.label,
          due_date: p.due_date,
        })),
      };
      if (isEdit) {
        await axios.put(`${API}/admin/owner-contracts/${initialData.id}`, payload, { headers });
        toast.success('Contrat modifié');
      } else {
        await axios.post(`${API}/admin/owner-contracts`, payload, { headers });
        toast.success('Contrat créé');
      }
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const SectionTitle = ({ children, icon: Icon }) => (
    <div className="flex items-center gap-2 mb-3 mt-6 first:mt-0">
      {Icon && <Icon className="h-4 w-4 text-[#C4724F]" />}
      <h3 className="text-sm font-bold text-[#0A2540] uppercase tracking-wide">{children}</h3>
    </div>
  );

  return (
    <Card className="border-[#C4724F]/20">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-[#0A2540]">{isEdit ? 'Modifier le contrat' : 'Nouveau contrat propriétaire'}</h2>
          <Button variant="ghost" size="sm" onClick={onCancel}><X className="h-4 w-4" /></Button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-1">
          {/* Owner */}
          <SectionTitle icon={User}>Propriétaire</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Nom complet *</Label>
              <Input value={form.owner_name} onChange={e => set('owner_name', e.target.value)} placeholder="Gérald CLAMARON" data-testid="owner-name-input" />
            </div>
            <div>
              <Label className="text-xs">Email *</Label>
              <Input type="email" value={form.owner_email} onChange={e => set('owner_email', e.target.value)} placeholder="gerald@example.com" data-testid="owner-email-input" />
            </div>
            <div>
              <Label className="text-xs">Adresse</Label>
              <Input value={form.owner_address} onChange={e => set('owner_address', e.target.value)} placeholder="Adresse complète" />
            </div>
            <div>
              <Label className="text-xs">Téléphone</Label>
              <Input value={form.owner_phone} onChange={e => set('owner_phone', e.target.value)} placeholder="+33 6 ..." />
            </div>
            <div>
              <Label className="text-xs">Assurance navire (compagnie)</Label>
              <Input value={form.owner_insurance_company} onChange={e => set('owner_insurance_company', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">N° contrat assurance</Label>
              <Input value={form.owner_insurance_number} onChange={e => set('owner_insurance_number', e.target.value)} />
            </div>
          </div>

          {/* Vessel */}
          <SectionTitle icon={Ship}>Navire</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Nom du navire *</Label>
              <Input value={form.vessel_name} onChange={e => set('vessel_name', e.target.value)} placeholder="LUCY" data-testid="vessel-name-input" />
            </div>
            <div>
              <Label className="text-xs">Modèle</Label>
              <Input value={form.vessel_model} onChange={e => set('vessel_model', e.target.value)} placeholder="Sunreef 50" />
            </div>
            <div>
              <Label className="text-xs">Année</Label>
              <Input value={form.vessel_year} onChange={e => set('vessel_year', e.target.value)} placeholder="2023" />
            </div>
            <div>
              <Label className="text-xs">N° d'enregistrement</Label>
              <Input value={form.vessel_registration} onChange={e => set('vessel_registration', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">N° WIN</Label>
              <Input value={form.vessel_win_number} onChange={e => set('vessel_win_number', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Port d'attache</Label>
              <Input value={form.vessel_home_port} onChange={e => set('vessel_home_port', e.target.value)} placeholder="Ex: Cannes, Ajaccio..." data-testid="vessel-home-port-input" />
            </div>
          </div>

          {/* Contract details */}
          <SectionTitle icon={Calendar}>Période et conditions</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Saison</Label>
              <Input value={form.season} onChange={e => set('season', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Date de début</Label>
              <Input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} data-testid="start-date-input" />
            </div>
            <div>
              <Label className="text-xs">Date de fin</Label>
              <Input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} data-testid="end-date-input" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div>
              <Label className="text-xs">Zone de navigation</Label>
              <Input value={form.navigation_zone} onChange={e => set('navigation_zone', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Juridiction</Label>
              <Input value={form.jurisdiction} onChange={e => set('jurisdiction', e.target.value)} />
            </div>
          </div>

          {/* Financial */}
          <SectionTitle icon={Banknote}>Conditions financières</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Montant total (EUR) *</Label>
              <Input type="number" value={form.total_amount} onChange={e => set('total_amount', e.target.value)} placeholder="40000" data-testid="total-amount-input" />
            </div>
            <div>
              <Label className="text-xs">Montant en lettres</Label>
              <Input value={form.total_amount_words} onChange={e => set('total_amount_words', e.target.value)} placeholder="Quarante mille euros" />
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-bold">Échéancier de paiement</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addPayment} className="text-xs gap-1">
                <Plus className="h-3 w-3" /> Ajouter
              </Button>
            </div>
            {payments.map((p, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <Input type="number" value={p.amount} onChange={e => setPayment(i, 'amount', e.target.value)}
                  placeholder="Montant" className="w-28" />
                <Input value={p.label} onChange={e => setPayment(i, 'label', e.target.value)}
                  placeholder={`Échéance ${i + 1} (ex: À la signature)`} className="flex-1" />
                <Input type="date" value={p.due_date} onChange={e => setPayment(i, 'due_date', e.target.value)}
                  className="w-40" />
                {payments.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => removePayment(i)}>
                    <X className="h-3 w-3 text-red-400" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Exploitant (collapsible) */}
          <details className="mt-4 border rounded-lg p-3 bg-gray-50">
            <summary className="text-xs font-bold text-[#0A2540] cursor-pointer uppercase tracking-wide">
              Exploitant (SOGNUDIMARE) — pré-rempli
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div>
                <Label className="text-xs">Nom</Label>
                <Input value={form.exploitant_name} onChange={e => set('exploitant_name', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Société</Label>
                <Input value={form.exploitant_company} onChange={e => set('exploitant_company', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Adresse</Label>
                <Input value={form.exploitant_address} onChange={e => set('exploitant_address', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">SIRET</Label>
                <Input value={form.exploitant_siret} onChange={e => set('exploitant_siret', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Code APE</Label>
                <Input value={form.exploitant_ape} onChange={e => set('exploitant_ape', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Assurance RC Pro</Label>
                <Input value={form.exploitant_insurance_company} onChange={e => set('exploitant_insurance_company', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">N° Police</Label>
                <Input value={form.exploitant_insurance_number} onChange={e => set('exploitant_insurance_number', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">N° Marin professionnel</Label>
                <Input value={form.exploitant_sailor_number} onChange={e => set('exploitant_sailor_number', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Patente</Label>
                <Input value={form.exploitant_patent} onChange={e => set('exploitant_patent', e.target.value)} />
              </div>
            </div>
          </details>

          <div className="flex justify-end gap-3 pt-6 border-t mt-6">
            <Button type="button" variant="outline" onClick={onCancel}>Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-[#0A2540] hover:bg-[#0A2540]/90 gap-2" data-testid="create-contract-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {isEdit ? 'Enregistrer les modifications' : 'Créer le contrat'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ─── CONTRACT DETAIL ───
function ContractDetail({ contract, onBack, onRefresh, onEdit, onDuplicate }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [sending, setSending] = useState(false);
  const [signingExploitant, setSigningExploitant] = useState(false);
  const [showSignPad, setShowSignPad] = useState(false);
  const [showCommission, setShowCommission] = useState(false);
  const [financialData, setFinancialData] = useState(null);
  const [savingCommission, setSavingCommission] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  const c = contract;
  const cc = c.commission_config || {};
  const st = statusConfig[c.status] || statusConfig.draft;
  const signUrl = `${window.location.origin}/contrat-proprietaire/${c.sign_token}`;

  const [commForm, setCommForm] = useState({
    enabled: cc.enabled || false,
    rate: cc.rate || 10,
    passenger_threshold: cc.passenger_threshold || 0,
    avg_price_per_passenger: cc.avg_price_per_passenger || 1990,
    max_capacity_per_week: cc.max_capacity_per_week || 8,
    fill_rate_estimate: cc.fill_rate_estimate || 50,
    realized_revenue: cc.realized_revenue || 0,
    realized_passengers: cc.realized_passengers || 0,
    realized_weeks: cc.realized_weeks || 0,
  });

  useEffect(() => {
    if (cc.enabled) {
      axios.get(`${API}/admin/owner-contracts/${c.id}/financial`, { headers })
        .then(r => setFinancialData(r.data))
        .catch(() => {});
    }
  }, [c.id, cc.enabled]);

  const saveCommission = async () => {
    setSavingCommission(true);
    try {
      await axios.put(`${API}/admin/owner-contracts/${c.id}/commission`, commForm, { headers });
      toast.success('Commission mise à jour');
      onRefresh();
    } catch (err) { toast.error('Erreur'); }
    finally { setSavingCommission(false); }
  };

  const handleSend = async () => {
    if (!c.owner?.email) { toast.error("Email du propriétaire manquant"); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/owner-contracts/${c.id}/send`, {}, { headers });
      toast.success(res.data.message);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSending(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce contrat ?')) return;
    try {
      await axios.delete(`${API}/admin/owner-contracts/${c.id}`, { headers });
      toast.success('Contrat supprimé');
      onBack();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDuplicate = async () => {
    try {
      await axios.post(`${API}/admin/owner-contracts/${c.id}/duplicate`, {}, { headers });
      toast.success('Contrat dupliqué');
      onDuplicate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleResetSignatures = async () => {
    if (!window.confirm('Réinitialiser toutes les signatures ? Le propriétaire devra re-signer avec un nouveau lien.')) return;
    try {
      await axios.post(`${API}/admin/owner-contracts/${c.id}/reset-signatures`, {}, { headers });
      toast.success('Signatures réinitialisées — nouveau lien de signature généré');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const markPaid = async (idx) => {
    try {
      await axios.post(`${API}/admin/owner-contracts/${c.id}/payment/${idx}/mark-paid`, { method: 'virement' }, { headers });
      toast.success('Échéance marquée comme payée');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const downloadPdf = () => {
    window.open(`${API}/admin/owner-contracts/${c.id}/pdf?token=${token}`, '_blank');
  };

  const copySignUrl = () => {
    navigator.clipboard.writeText(signUrl);
    toast.success('Lien copié');
  };

  // Canvas drawing for exploitant signature
  const startDraw = (e) => {
    drawing.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) * scaleX;
    const y = ((e.touches ? e.touches[0].clientY : e.clientY) - rect.top) * scaleY;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) * scaleX;
    const y = ((e.touches ? e.touches[0].clientY : e.clientY) - rect.top) * scaleY;
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#0A2540';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };
  const endDraw = () => { drawing.current = false; };
  const clearCanvas = () => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  };

  const handleSignExploitant = async () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let hasDrawing = false;
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] > 0) { hasDrawing = true; break; }
    }
    if (!hasDrawing) { toast.error('Veuillez signer'); return; }
    setSigningExploitant(true);
    try {
      const sigData = canvas.toDataURL('image/png');
      await axios.post(`${API}/admin/owner-contracts/${c.id}/sign-exploitant`, { signature: sigData }, { headers });
      toast.success('Contrat signé par l\'exploitant');
      setShowSignPad(false);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSigningExploitant(false); }
  };

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack} className="gap-1 text-gray-500 mb-2" data-testid="back-to-list-btn">
        ← Retour à la liste
      </Button>

      {/* Header card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <span className="text-xs font-mono text-gray-400">{c.reference}</span>
              <h2 className="text-xl font-bold text-[#0A2540]">{c.vessel?.name} — {c.vessel?.model}</h2>
              <p className="text-sm text-gray-500">Saison {c.season} &middot; {formatDateFr(c.start_date)} → {formatDateFr(c.end_date)}</p>
              {c.vessel?.home_port && <p className="text-xs text-gray-400">Port d'attache : {c.vessel.home_port}</p>}
            </div>
            <Badge className={st.style} data-testid="contract-detail-status">{st.label}</Badge>
          </div>

          {/* Parties */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-[#C4724F] uppercase mb-2">Propriétaire</p>
              <p className="font-bold text-[#0A2540]">{c.owner?.name}</p>
              {c.owner?.address && <p className="text-sm text-gray-500">{c.owner.address}</p>}
              {c.owner?.email && <p className="text-xs text-gray-400 flex items-center gap-1"><Mail className="h-3 w-3" />{c.owner.email}</p>}
              {c.owner?.phone && <p className="text-xs text-gray-400 flex items-center gap-1"><Phone className="h-3 w-3" />{c.owner.phone}</p>}
              {c.owner_signed_at && (
                <div className="flex items-center gap-1 mt-2">
                  <Check className="h-3.5 w-3.5 text-green-600" />
                  <span className="text-xs text-green-700">Signé le {formatDateTimeFr(c.owner_signed_at)}</span>
                </div>
              )}
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-[#C4724F] uppercase mb-2">Exploitant</p>
              <p className="font-bold text-[#0A2540]">{c.exploitant?.name}</p>
              <p className="text-sm text-gray-500">{c.exploitant?.company}</p>
              {c.exploitant?.siret && <p className="text-xs text-gray-400">SIRET : {c.exploitant.siret}</p>}
              {c.exploitant?.sailor_number && <p className="text-xs text-gray-400">N° Marin : {c.exploitant.sailor_number} / Patente : {c.exploitant.patent}</p>}
              {c.exploitant_signed_at && (
                <div className="flex items-center gap-1 mt-2">
                  <Check className="h-3.5 w-3.5 text-green-600" />
                  <span className="text-xs text-green-700">Signé le {formatDateTimeFr(c.exploitant_signed_at)}</span>
                </div>
              )}
            </div>
          </div>

          {/* RIB if provided */}
          {c.owner_rib?.iban && (
            <div className="bg-emerald-50 rounded-xl p-4 mb-6 border border-emerald-200">
              <p className="text-xs font-semibold text-emerald-700 uppercase mb-2">RIB du propriétaire</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-500">Titulaire :</span> <strong>{c.owner_rib.holder}</strong></div>
                <div><span className="text-gray-500">Banque :</span> <strong>{c.owner_rib.bank}</strong></div>
                <div className="break-all"><span className="text-gray-500">IBAN :</span> <strong className="font-mono text-xs">{c.owner_rib.iban}</strong></div>
                <div><span className="text-gray-500">BIC :</span> <strong className="font-mono text-xs">{c.owner_rib.bic}</strong></div>
              </div>
            </div>
          )}

          {/* Payment schedule */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-[#0A2540] mb-3 flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-[#C4724F]" /> Échéancier — {formatMoney(c.total_amount)}
            </h3>
            <div className="space-y-2">
              {(c.payment_schedule || []).map((p, i) => (
                <div key={i} className={`flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-lg border gap-2 ${p.paid ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${p.paid ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                      {p.paid ? <Check className="h-4 w-4" /> : i + 1}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{formatMoney(p.amount)}</p>
                      <p className="text-xs text-gray-500">{p.label}{p.due_date ? ` — ${p.due_date}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:ml-0 ml-11">
                    {p.paid ? (
                      <span className="text-xs text-emerald-600">Payé le {formatDateTimeFr(p.paid_at)}</span>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => markPaid(i)} className="text-xs gap-1" data-testid={`mark-paid-${i}`}>
                        <Banknote className="h-3 w-3" /> Marquer payé
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Commission variable */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="h-4 w-4 text-[#C4724F]" />
              <span className="text-sm font-bold text-[#0A2540]">Rétrocession & Commission variable</span>
              {cc.enabled && <Badge className="bg-emerald-100 text-emerald-700 ml-2">Active</Badge>}
              <Button variant="outline" size="sm" onClick={() => setShowCommission(!showCommission)} className="ml-auto gap-1 text-xs" data-testid="toggle-commission">
                <Pencil className="h-3 w-3" /> {showCommission ? 'Masquer les paramètres' : 'Modifier les paramètres'}
              </Button>
            </div>

            {showCommission && (
              <div className="mb-4 p-4 bg-gray-50 rounded-xl border space-y-3" data-testid="commission-config">
                <div className="flex items-center gap-3 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={commForm.enabled} onChange={e => setCommForm(p => ({...p, enabled: e.target.checked}))}
                      className="w-4 h-4 rounded border-gray-300" data-testid="commission-enabled-toggle" />
                    <span className="text-sm font-medium text-[#0A2540]">Activer la commission variable</span>
                  </label>
                </div>
                {commForm.enabled && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Taux de commission (%)</Label>
                        <Input type="number" value={commForm.rate} onChange={e => setCommForm(p => ({...p, rate: parseFloat(e.target.value) || 0}))} data-testid="commission-rate-input" />
                      </div>
                      <div>
                        <Label className="text-xs">Seuil passagers (acquis)</Label>
                        <Input type="number" value={commForm.passenger_threshold} onChange={e => setCommForm(p => ({...p, passenger_threshold: parseInt(e.target.value) || 0}))} data-testid="commission-threshold-input" />
                      </div>
                      <div>
                        <Label className="text-xs">Prix moyen / passager</Label>
                        <Input type="number" value={commForm.avg_price_per_passenger} onChange={e => setCommForm(p => ({...p, avg_price_per_passenger: parseFloat(e.target.value) || 0}))} />
                      </div>
                      <div>
                        <Label className="text-xs">Capacité max / semaine</Label>
                        <Input type="number" value={commForm.max_capacity_per_week} onChange={e => setCommForm(p => ({...p, max_capacity_per_week: parseInt(e.target.value) || 0}))} />
                      </div>
                      <div>
                        <Label className="text-xs">Taux remplissage estimé (%)</Label>
                        <Input type="number" value={commForm.fill_rate_estimate} onChange={e => setCommForm(p => ({...p, fill_rate_estimate: parseFloat(e.target.value) || 0}))} />
                      </div>
                    </div>
                    <div className="border-t pt-3 mt-2">
                      <p className="text-xs font-bold text-[#0A2540] uppercase mb-2">Données réalisées (à mettre à jour)</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs">CA réalisé (EUR)</Label>
                          <Input type="number" value={commForm.realized_revenue} onChange={e => setCommForm(p => ({...p, realized_revenue: parseFloat(e.target.value) || 0}))} data-testid="realized-revenue-input" />
                        </div>
                        <div>
                          <Label className="text-xs">Passagers réalisés</Label>
                          <Input type="number" value={commForm.realized_passengers} onChange={e => setCommForm(p => ({...p, realized_passengers: parseInt(e.target.value) || 0}))} data-testid="realized-passengers-input" />
                        </div>
                        <div>
                          <Label className="text-xs">Semaines réalisées</Label>
                          <Input type="number" value={commForm.realized_weeks} onChange={e => setCommForm(p => ({...p, realized_weeks: parseInt(e.target.value) || 0}))} data-testid="realized-weeks-input" />
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Button size="sm" onClick={saveCommission} disabled={savingCommission} className="bg-[#C4724F] hover:bg-[#b5634a] gap-1" data-testid="save-commission-btn">
                        {savingCommission ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Enregistrer
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Financial Dashboard - always visible when commission active */}
            {financialData?.enabled && <FinancialDashboard data={financialData} viewMode="admin" />}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-4 border-t">
            <Button variant="outline" size="sm" onClick={downloadPdf} className="gap-1" data-testid="download-pdf-btn">
              <Download className="h-4 w-4" /> <span className="hidden sm:inline">PDF</span>
            </Button>
            <Button variant="outline" size="sm" onClick={onEdit} className="gap-1" data-testid="edit-contract-btn">
              <Pencil className="h-4 w-4" /> <span className="hidden sm:inline">Modifier</span>
            </Button>
            <Button variant="outline" size="sm" onClick={handleDuplicate} className="gap-1" data-testid="duplicate-contract-btn">
              <CopyPlus className="h-4 w-4" /> <span className="hidden sm:inline">Dupliquer</span>
            </Button>
            {c.status !== 'signed' && (
              <Button size="sm" onClick={handleSend} disabled={sending} className="gap-1 bg-blue-600 hover:bg-blue-700" data-testid="send-contract-btn">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                <span className="hidden sm:inline">Envoyer</span>
              </Button>
            )}
            {!c.exploitant_signed_at && (
              <Button size="sm" onClick={() => setShowSignPad(true)} className="gap-1 bg-[#C4724F] hover:bg-[#b5634a]" data-testid="sign-exploitant-btn">
                <FileSignature className="h-4 w-4" /> <span className="hidden sm:inline">Signer</span>
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={copySignUrl} className="gap-1" data-testid="copy-sign-url-btn">
              <Copy className="h-4 w-4" /> <span className="hidden sm:inline">Lien</span>
            </Button>
            {(c.owner_signed_at || c.exploitant_signed_at) && (
              <Button size="sm" variant="outline" onClick={handleResetSignatures} className="gap-1 text-orange-600 border-orange-300 hover:bg-orange-50" data-testid="reset-signatures-btn">
                <RotateCcw className="h-4 w-4" /> Faire re-signer
              </Button>
            )}
            <Button size="sm" variant="destructive" onClick={handleDelete} className="gap-1 ml-auto" data-testid="delete-contract-btn">
              <Trash2 className="h-4 w-4" /> Supprimer
            </Button>
          </div>

          {/* Exploitant sign pad */}
          {showSignPad && (
            <div className="mt-6 p-4 border-2 border-[#C4724F] rounded-xl bg-[#C4724F]/5" data-testid="exploitant-sign-section">
              <h4 className="text-sm font-bold text-[#0A2540] mb-3">Signature de l'exploitant (SOGNUDIMARE)</h4>
              <div className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-white">
                <canvas
                  ref={canvasRef}
                  width={560}
                  height={160}
                  className="w-full touch-none cursor-crosshair"
                  onMouseDown={startDraw}
                  onMouseMove={draw}
                  onMouseUp={endDraw}
                  onMouseLeave={endDraw}
                  onTouchStart={startDraw}
                  onTouchMove={draw}
                  onTouchEnd={endDraw}
                  data-testid="exploitant-signature-canvas"
                />
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button onClick={clearCanvas} className="text-xs text-gray-400 hover:text-red-500">Effacer</button>
                <div className="flex-1" />
                <Button variant="outline" size="sm" onClick={() => setShowSignPad(false)}>Annuler</Button>
                <Button size="sm" onClick={handleSignExploitant} disabled={signingExploitant} className="bg-[#0A2540] hover:bg-[#0A2540]/90 gap-1" data-testid="confirm-exploitant-sign-btn">
                  {signingExploitant ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  Confirmer
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// ─── OWNER ACCOUNTS MANAGEMENT ───
function OwnerAccountsTab() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: 'owner2026', phone: '', vessel_names: '', mmsi: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/owner-accounts`, { headers });
      setAccounts(res.data);
    } catch {}
  }, []);

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({ name: '', email: '', password: '', phone: '', vessel_names: '', mmsi: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const startEdit = (a) => {
    setForm({
      name: a.name || '',
      email: a.email || '',
      password: '',
      phone: a.phone || '',
      vessel_names: (a.vessel_names || []).join(', '),
      mmsi: a.mmsi || '',
    });
    setEditingId(a.id);
    setShowForm(true);
  };

  const startCreate = () => {
    setForm({ name: '', email: '', password: 'owner2026', phone: '', vessel_names: '', mmsi: '' });
    setEditingId(null);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) { toast.error('Nom et email requis'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        vessel_names: form.vessel_names.split(',').map(v => v.trim()).filter(Boolean),
      };
      if (!payload.password) delete payload.password;

      if (editingId) {
        await axios.put(`${API}/admin/owner-accounts/${editingId}`, payload, { headers });
        toast.success('Compte modifié');
      } else {
        if (!payload.password) payload.password = 'owner2026';
        await axios.post(`${API}/admin/owner-accounts`, payload, { headers });
        toast.success('Compte créé');
      }
      resetForm();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const deleteAccount = async (id) => {
    if (!window.confirm('Supprimer ce compte ?')) return;
    try {
      await axios.delete(`${API}/admin/owner-accounts/${id}`, { headers });
      toast.success('Compte supprimé');
      load();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div data-testid="owner-accounts-tab">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{accounts.length} compte{accounts.length > 1 ? 's' : ''}</p>
        <Button size="sm" onClick={startCreate} className="bg-[#0A2540] hover:bg-[#0A2540]/90 gap-1" data-testid="new-owner-account-btn">
          <Plus className="h-4 w-4" /> Nouveau compte
        </Button>
      </div>

      {showForm && (
        <Card className="mb-4 border-[#D4AF37]/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-bold text-[#0A2540]">{editingId ? 'Modifier le compte' : 'Nouveau compte'}</p>
              <Button variant="ghost" size="sm" onClick={resetForm}><X className="h-4 w-4" /></Button>
            </div>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Nom *</Label>
                <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Gérald CLAMARON" data-testid="account-name-input" />
              </div>
              <div>
                <Label className="text-xs">Email *</Label>
                <Input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="gerald@example.com" data-testid="account-email-input" />
              </div>
              <div>
                <Label className="text-xs">{editingId ? 'Nouveau mot de passe (vide = inchangé)' : 'Mot de passe'}</Label>
                <Input value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))} placeholder={editingId ? 'Laisser vide pour ne pas changer' : 'owner2026'} data-testid="account-password-input" />
              </div>
              <div>
                <Label className="text-xs">Téléphone</Label>
                <Input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} />
              </div>
              <div>
                <Label className="text-xs">Navires (séparés par virgule)</Label>
                <Input value={form.vessel_names} onChange={e => setForm(p => ({...p, vessel_names: e.target.value}))} placeholder="SUNREEF 50, LUCY" data-testid="account-vessels-input" />
              </div>
              <div>
                <Label className="text-xs">MMSI (géolocalisation)</Label>
                <Input value={form.mmsi} onChange={e => setForm(p => ({...p, mmsi: e.target.value}))} placeholder="227925410" data-testid="account-mmsi-input" />
              </div>
              <div className="md:col-span-3 flex justify-end gap-2">
                <Button type="button" variant="outline" size="sm" onClick={resetForm}>Annuler</Button>
                <Button type="submit" size="sm" disabled={saving} className="bg-[#D4AF37] hover:bg-[#c4a030] gap-1" data-testid="create-account-btn">
                  {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : editingId ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                  {editingId ? 'Enregistrer' : 'Créer'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {accounts.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <User className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Aucun compte propriétaire</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {accounts.map(a => (
            <Card key={a.id}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#D4AF37] rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {a.name?.[0] || '?'}
                  </div>
                  <div>
                    <p className="font-medium text-[#0A2540]">{a.name}</p>
                    <p className="text-xs text-gray-500">{a.email} {a.mmsi ? `— MMSI: ${a.mmsi}` : ''}</p>
                    {a.vessel_names?.length > 0 && <p className="text-xs text-gray-400">Navires : {a.vessel_names.join(', ')}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-100 text-emerald-700">{a.status}</Badge>
                  <Button size="sm" variant="ghost" onClick={() => startEdit(a)} className="text-blue-500 hover:text-blue-700" data-testid={`edit-account-${a.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteAccount(a.id)} className="text-red-400 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200 text-xs text-blue-700">
        Les propriétaires accèdent à leur espace via <strong>{window.location.origin}/proprietaire</strong>
      </div>
    </div>
  );
}


// ─── MAIN PAGE ───
export default function AdminOwnerContracts() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [tab, setTab] = useState('contracts');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/owner-contracts`, { headers });
      setContracts(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/admin/login');
      else toast.error('Erreur de chargement');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, []);

  const selected = contracts.find(c => c.id === selectedId);
  const editing = contracts.find(c => c.id === editId);

  const refreshSelected = async () => {
    await load();
  };

  if (loading) return (
    <AdminLayout title="Contrats Propriétaires">
      <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 text-[#C4724F] animate-spin" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout title="Contrats Propriétaires">
      <div data-testid="admin-owner-contracts-page">
        {/* Tabs */}
        {!selectedId && !editId && !showCreate && (
          <div className="flex gap-1 mb-6 border-b">
            <button onClick={() => setTab('contracts')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'contracts' ? 'border-[#C4724F] text-[#0A2540]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
              data-testid="tab-contracts">
              Contrats
            </button>
            <button onClick={() => setTab('accounts')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'accounts' ? 'border-[#C4724F] text-[#0A2540]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
              data-testid="tab-accounts">
              Comptes Propriétaires
            </button>
          </div>
        )}

        {/* Accounts tab */}
        {tab === 'accounts' && !selectedId && !editId && !showCreate ? (
          <OwnerAccountsTab />
        ) : editId && editing ? (
          <ContractForm
            initialData={editing}
            onDone={() => { setEditId(null); load(); }}
            onCancel={() => setEditId(null)}
          />
        ) : selectedId && selected ? (
          <ContractDetail
            contract={selected}
            onBack={() => setSelectedId(null)}
            onRefresh={refreshSelected}
            onEdit={() => { setEditId(selectedId); setSelectedId(null); }}
            onDuplicate={() => { setSelectedId(null); load(); }}
          />
        ) : showCreate ? (
          <ContractForm
            onDone={() => { setShowCreate(false); load(); }}
            onCancel={() => setShowCreate(false)}
          />
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-sm text-gray-500">{contracts.length} contrat{contracts.length > 1 ? 's' : ''}</p>
              </div>
              <Button onClick={() => setShowCreate(true)} className="bg-[#0A2540] hover:bg-[#0A2540]/90 gap-2" data-testid="new-contract-btn">
                <Plus className="h-4 w-4" /> Nouveau contrat
              </Button>
            </div>

            {/* List */}
            {contracts.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Ship className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Aucun contrat propriétaire</h3>
                  <p className="text-sm text-gray-500 mb-4">Créez votre premier contrat de mise à disposition</p>
                  <Button onClick={() => setShowCreate(true)} className="bg-[#C4724F] hover:bg-[#b5634a] gap-2">
                    <Plus className="h-4 w-4" /> Créer un contrat
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {contracts.map(c => {
                  const st = statusConfig[c.status] || statusConfig.draft;
                  return (
                    <Card key={c.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedId(c.id)} data-testid={`contract-row-${c.id}`}>
                      <CardContent className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#0A2540] rounded-lg flex items-center justify-center flex-shrink-0">
                              <Anchor className="h-5 w-5 text-[#D4AF37]" />
                            </div>
                            <div>
                              <p className="font-bold text-[#0A2540]">{c.vessel?.name || 'Navire'} — {c.vessel?.model || ''}</p>
                              <p className="text-xs text-gray-500">
                                {c.reference} &middot; Propriétaire : {c.owner?.name} &middot; Saison {c.season}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-bold text-[#0A2540]">{formatMoney(c.total_amount)}</span>
                            <Badge className={st.style}>{st.label}</Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
